import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import LockedFeature from "@/components/LockedFeature";
import { Radio, Loader2, ShieldCheck } from "lucide-react";

export default function Evidence() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/evidence").then((r) => setData(r.data)), []);
  useEffect(() => { load(); api.get("/billing/status").then((r) => setActive(r.data.active)); }, [load]);

  const sweep = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/evidence/collect");
      toast.success(`Captured ${res.captured} evidence artifacts.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sweep failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;
  if (active === false) return <div className="p-8 max-w-3xl"><LockedFeature feature="Continuous Evidence Collector" /></div>;

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline mb-1">Feature C</div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Continuous Evidence Collector</h1>
          <p className="text-slate-500 text-sm mt-1">Timestamped, hash-verified, append-only evidence — your daily audit trail.</p>
        </div>
        <button onClick={sweep} disabled={busy} data-testid="collect-btn"
          className="bg-brand text-white px-5 py-2.5 rounded-md text-sm font-medium flex items-center gap-2 hover:bg-brand-secondary transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radio className="h-4 w-4" />} {busy ? "Collecting…" : "Run Collector Sweep"}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm" data-testid="audit-ready">
          <div className="overline mb-1">Audit Ready</div>
          <div className="font-display font-black text-3xl text-success">{data.audit_ready_pct}%</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
          <div className="overline mb-1">Controls Covered</div>
          <div className="font-display font-black text-3xl">{data.covered_controls}/{data.monitored_controls}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
          <div className="overline mb-1">Artifacts Logged</div>
          <div className="font-display font-black text-3xl">{data.log.length}</div>
        </div>
      </div>

      <div className="bg-brand rounded-md shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-700/60 flex items-center gap-2 text-slate-300">
          <ShieldCheck className="h-4 w-4 text-brand-accent" />
          <span className="font-mono text-xs">cmmc-shield://evidence-collector — append-only log</span>
        </div>
        <div className="p-5 font-mono text-xs max-h-[480px] overflow-auto" data-testid="evidence-log">
          {data.log.length === 0 ? (
            <div className="text-slate-500">No evidence captured yet. Run a collector sweep to populate the log.</div>
          ) : data.log.map((e) => (
            <div key={e.id} className="flex gap-3 py-1 text-slate-300" data-testid={`evlog-${e.id}`}>
              <span className="text-slate-500 shrink-0">{new Date(e.captured_at).toISOString().replace("T", " ").slice(0, 19)}</span>
              <span className="text-success shrink-0">[{e.objective_id}]</span>
              <span className="text-white shrink-0">{e.kind}</span>
              <span className="text-slate-300 truncate">{e.label}</span>
              <span className="text-slate-600 ml-auto shrink-0">{e.sha256.slice(0, 24)}…</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
